import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Subscription } from "rxjs";
import { fadeInRight400ms } from "src/@vex/animations/fade-in-right.animation";
import { fadeInUp400ms } from "src/@vex/animations/fade-in-up.animation";
import { scaleIn400ms } from "src/@vex/animations/scale-in.animation";
import { TableColumn } from "src/@vex/interfaces/table-column.interface";
import { PictureService } from "src/app/_services/picture.service";
import {
  AuctionWithRelations,
  AuthorWithRelations,
  ContactWithRelations,
  ExhibitionWithRelations,
  InventoryWithRelations,
  Item,
  ItemOwnerWithRelations,
  ItemWithRelations,
  LocationWithRelations,
  PublicationWithRelations,
  SeriesWithRelations,
  TagGroup,
  TagWithRelations,
} from "src/app/api/models";
import { ItemPreviewComponent } from "../item-preview/item-preview.component";
import { AuthorService } from "src/app/shared/services/author.service";
import {
  ItemService,
  AVAILABLE_SORT_COLUMNS,
} from "src/app/_services/item.service";
import { LocationService } from "src/app/shared/services/location.service";
import { OwnerService } from "src/app/shared/services/owner.service";
import { SnackBarService } from "src/app/shared/services/snack-bar.service";
import { StringService } from "src/app/_services/string.service";

export type ListEnumType =
  | "locations"
  | "tags"
  | "authors"
  | "owners"
  | "series"
  | "exhibitions"
  | "auctions"
  | "publications";

export interface ItemsQuickListComponentData {
  title: string;
  type: ListEnumType;
  items: ItemWithRelations[];
  exhibition?: ExhibitionWithRelations;
  author?: AuthorWithRelations;
  owner?: ContactWithRelations;
  series?: SeriesWithRelations;
  tagGroup?: TagGroup;
  tag?: TagWithRelations;
  tags?: TagWithRelations[];
  location?: LocationWithRelations;
  locations?: LocationWithRelations[];
  inventoryLocations?: LocationWithRelations[];
  inventory?: InventoryWithRelations;
  auction?: AuctionWithRelations;
  publication?: PublicationWithRelations;
}

@UntilDestroy()
@Component({
  selector: "art-items-quick-list",
  templateUrl: "./items-quick-list.component.html",
  styleUrls: ["./items-quick-list.component.scss"],
  animations: [scaleIn400ms, fadeInRight400ms, fadeInUp400ms],
  standalone: false,
})
export class ItemsQuickListComponent implements OnInit, AfterViewInit {
  tableData: ItemWithRelations[] = [];
  title: string;
  type: ListEnumType;
  inventory: InventoryWithRelations;
  author: AuthorWithRelations;
  owner: ContactWithRelations;
  series: SeriesWithRelations;
  tagGroup: TagGroup;
  tag: TagWithRelations;
  tags: TagWithRelations[];
  location: LocationWithRelations;
  locations: LocationWithRelations[];
  inventoryLocations: LocationWithRelations[];
  auction: AuctionWithRelations;
  publication: PublicationWithRelations;

  ui: {
    title: string;
    titleLabel: string;
    subtitle: string;
    subtitleLabel: string;
    description: string;
    tableTitle: string;
  } = {
    title: "",
    titleLabel: "",
    subtitle: "",
    subtitleLabel: "",
    description: "",
    tableTitle: "",
  };

  dataSource: MatTableDataSource<ItemWithRelations> = new MatTableDataSource(
    [],
  );

  searchCtrl = new UntypedFormControl();
  searchCtrlChangeSubscription: Subscription = new Subscription();

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  DEFAULT_SORT: Sort = { active: "inventoryNumber", direction: "asc" };
  TABLE_COLUMNS: TableColumn<ItemWithRelations>[] = [
    {
      label: "Inventární číslo",
      property: "inventoryCode",
      type: "text",
      visible: true,
      cssClasses: ["font-light w-1/12 max-w-12 min-w-12"],
    },
    {
      label: "Náhled",
      property: "preview",
      type: "image",
      visible: true,
    },
    {
      label: "Název",
      property: "originalTitle",
      type: "text",
      visible: true,
      cssClasses: [
        "font-semibold w-[45rem] max-w-[45rem] min-w-[45rem] truncate",
      ],
    },
    {
      label: "Autoři",
      property: "authors",
      type: "text",
      visible: true,
      cssClasses: ["font-semibold"],
    },
    {
      label: "Datace",
      property: "dating",
      type: "text",
      visible: true,
    },
    {
      label: "Datace k řazení",
      property: "datingOrder",
      type: "text",
      visible: true,
    },
    {
      label: "Druh majitele",
      property: "owner",
      type: "text",
      visible: false,
      cssClasses: ["font-semibold"],
    },
    {
      label: "Datum nabytí",
      property: "yearPurchase",
      type: "text",
      visible: false,
    },
    {
      label: "Lokace",
      property: "location",
      type: "text",
      visible: false,
    },
    {
      label: "Klíčová slova",
      property: "tag",
      type: "text",
      visible: false,
    },
    { label: "Akce", property: "actions", type: "button", visible: true },
  ];
  usedTableColumns: TableColumn<Item>[] = [];
  availableSortColumns = AVAILABLE_SORT_COLUMNS;

  constructor(
    private router: Router,
    @Inject(MAT_DIALOG_DATA) public data: ItemsQuickListComponentData,
    private dialogRef: MatDialogRef<ItemsQuickListComponent>,
    public itemService: ItemService,
    public pictureService: PictureService,
    public locationService: LocationService,
    private dialog: MatDialog,
    private snackBar: SnackBarService,
    private authorService: AuthorService,
    private _stringService: StringService,
    public ownerService: OwnerService,
  ) {
    this.prepareFilterPredicate();
  }
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.dataSource.sort = this.sort;
      this.dataSource.sortData = this.itemService.customSortItems.bind(
        this.itemService,
      );
    });
  }

  ngOnInit(): void {
    if (this.data) {
      this.type = this.data.type;
      this.inventory = this.data.inventory || null;
      this.author = this.data.author || null;
      this.owner = this.data.owner || null;
      this.series = this.data.series || null;
      this.tagGroup = this.data.tagGroup || null;
      this.tag = this.data.tag || null;
      this.tags = this.data.tags || null;
      this.location = this.data.location || null;
      this.locations = this.data.locations || null;
      this.inventoryLocations = this.data.inventoryLocations || null;
      this.auction = this.data.auction || null;
      this.publication = this.data.publication || null;

      this.tableData = this.data.items;
      this.dataSource.data = this.tableData;
      this.title = this.data.title;
      this.ui.title = this.data.title;

      this.prepareVisibleColumns();
      this.prepareUIText();
      this.searchCtrlChangeSubscription = this.searchCtrl.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe((value) => this.onFilterChange(value));
    } else {
      console.error("No data provided for ItemsQuickListComponent");
      this.dialogRef.close();
    }
  }

  close() {
    this.dialogRef.close();
  }

  get visibleColumns() {
    const visibleColumns = this.usedTableColumns
      .filter((column) => column.visible)
      .map((column) => column.property);
    return visibleColumns;
  }

  prepareVisibleColumns(): void {
    if (this.inventory.authorId) {
      this.TABLE_COLUMNS.find(
        (column) => column.property === "authors",
      ).visible = false;
    }
    switch (this.type) {
      case "locations":
        if (this.locations?.length > 1) {
          this.TABLE_COLUMNS.find(
            (column) => column.property === "location",
          ).visible = true;
        }
        break;
      case "tags":
        if (this.tags?.length > 1) {
          this.TABLE_COLUMNS.find(
            (column) => column.property === "tag",
          ).visible = true;
        }
        break;
      case "authors": {
        this.TABLE_COLUMNS.find(
          (column) => column.property === "authors",
        ).visible = false;
        this.TABLE_COLUMNS.find(
          (column) => column.property === "location",
        ).visible = true;

        break;
      }
      case "series":
        // nothing to do
        break;
      case "exhibitions":
        // nothing to do
        break;
      case "auctions":
        // nothing to do
        break;
      case "publications":
        // nothing to do
        break;
      case "owners":
        this.TABLE_COLUMNS.find(
          (column) => column.property === "owner",
        ).visible = true;
        this.TABLE_COLUMNS.find(
          (column) => column.property === "yearPurchase",
        ).visible = true;
        break;
      default:
        break;
    }

    this.TABLE_COLUMNS.forEach((column) => {
      if (column.visible) {
        this.usedTableColumns.push(column);
      }
    });
  }

  trackByProperty<T>(index: number, column: TableColumn<T>) {
    return column.property;
  }

  /**
   * Navigates to the item detail page and closes the current dialog.
   *
   * @param {string} id - The ID of the item to navigate to.
   */
  async goToItemDetail(id: string) {
    // Navigate to the item detail page using the router
    await this.router.navigate(["/item", id]).then(() => {
      // Close the current dialog after navigation
      this.close();
    });
  }

  /**
   * Opens a dialog to display the details of the selected item.
   *
   * @param {ItemWithRelations} item - The item to display in the detail dialog.
   */
  prepareItemQuickDialog(item: ItemWithRelations) {
    try {
      this.dialog.open(ItemPreviewComponent, {
        data: {
          item,
          showAuthor: true,
          reloadItem: true,
          allowNotPublicPhoto: true,
        },
        width: "90vw",
      });
    } catch {
      this.snackBar.open("Nepodařilo se otevřít detail položky");
    }
  }

  private onFilterChange(value: string) {
    if (!this.dataSource) {
      return;
    }
    value = value.trim();
    this.dataSource.filter = value;
  }

  /**
   * Prepares the UI text based on the type of data provided.
   * This method configures the UI texts (title, subtitle, description, etc.)
   * according to the data type (locations, series or tags). It sets different text
   * values based on properties of the associated data.
   */
  private prepareUIText(): void {
    switch (this.type) {
      case "locations":
        this.ui.title = this.location.title;
        this.ui.titleLabel = "Lokace";
        this.ui.subtitle =
          this.location.parentId &&
          this.locationService.getLocationLabel(
            this.location,
            this.inventoryLocations,
          );
        this.ui.description = this.location.description;
        break;
      case "series":
        this.ui.title = this.series.lang.title;
        this.ui.titleLabel = "Série";
        this.ui.subtitle = this.series.type.titleCS;
        this.ui.subtitleLabel = "Typ";
        this.ui.description = this.series.lang.description;
        break;
      case "tags":
        this.ui.title = this.tag ? this.tag.titleCS : this.tagGroup.titleCS;
        this.ui.titleLabel = this.tag ? "Klíčové slovo" : "Skupina";
        this.ui.subtitle = this.tag ? this.tagGroup.titleCS : null;
        this.ui.subtitleLabel = this.tag && "Skupina";
        break;
      case "exhibitions":
        this.ui.titleLabel = "Výstava";
        break;
      case "auctions":
        this.ui.titleLabel = "Aukce";
        break;
      case "publications":
        this.ui.titleLabel = "Publikace";
        break;
      case "authors":
        this.ui.title = this.authorService.parseAuthorName(this.author);
        this.ui.titleLabel = "Autor";
        break;
      case "owners":
        this.ui.title = this.owner.title;
        this.ui.titleLabel = "Majitel";
        break;
      default:
        break;
    }
    this.ui.tableTitle = "Seznam položek";
  }

  /**
   * Prepares the custom filter predicate for the data source.
   *
   * The predicate compiles a searchable string from an item's key properties,
   * converts it and the user filter to lowercase, normalizes the strings to remove diacritics,
   * splits the filter into individual words, and then checks if every word is present
   * in the compiled string. This allows matching even when diacritics are omitted.
   */
  private prepareFilterPredicate() {
    this.dataSource.filterPredicate = (
      data: ItemWithRelations,
      filter: string,
    ) => {
      // Initialize dataString with an empty string.
      let dataString = "";

      // Append inventory code and original title.
      dataString += " " + data.inventoryCode;
      dataString += " " + data.originalTitle;

      // Append parsed author names (if available).
      dataString +=
        " " +
        data.authors
          ?.map((author) => this.authorService.parseAuthorName(author))
          .join(" ");

      // If filtering by locations and there are multiple locations, append location title and description.
      if (this.type === "locations" && this.locations.length > 1) {
        dataString += " " + data.location?.title;
        dataString += " " + data.location?.description;
      }

      // If filtering by tags and there are multiple tags, append the corresponding tags' titles.
      if (this.type === "tags" && this.tags.length > 1) {
        dataString +=
          " " +
          this.getItemCorrespondingTags(data)
            .map((tag) => tag.titleCS)
            .join(" ");
      }

      // Convert both dataString and filter to lowercase using Czech locale,
      // normalize them (NFD normalization) and remove diacritic marks.
      dataString = this._stringService
        .normalize(dataString)
        .toLocaleLowerCase("cs-CZ");
      filter = this._stringService.normalize(filter).toLocaleLowerCase("cs-CZ");

      // Split the filter string into individual words, removing extra spaces.
      const filterWords = filter
        .trim()
        .split(" ")
        .filter((word) => word);

      // Check if every filter word is present in the dataString.
      return filterWords.every((word) => dataString.includes(word));
    };
  }

  /**
   * Returns the list of tags that correspond to the given item's tags.
   *
   * This method filters the available tags and returns only those tags whose IDs
   * match any of the IDs present in the item's tags array.
   *
   * @param item - The item for which to retrieve the corresponding tags.
   * @returns An array of TagWithRelations that match the item's tags.
   */
  getItemCorrespondingTags(item: ItemWithRelations): TagWithRelations[] {
    // Filter the list of all tags to include only those that are present in the item's tags.
    const correspondingTags = this.tags.filter((tag) =>
      // Check if the current tag's id matches any of the ids in the item's tags array.
      item.tags?.some((a) => a.id === tag.id),
    );
    // Return the filtered list of corresponding tags.
    return correspondingTags;
  }

  getItemCorrespondingOwner(
    item: ItemWithRelations,
  ): ItemOwnerWithRelations | null {
    const owner: ItemOwnerWithRelations = item.owners?.find(
      (owner) => owner.contactId === this.owner?.id,
    );

    return owner;
  }
}
